import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  GraduationCap,
  Monitor,
  Timer,
  Sparkles,
  Briefcase,
  School,
  Infinity as InfinityIcon,
  BriefcaseBusiness,
  Sparkle,
  MessageSquareHeart,
  CheckCircle2,
  Target,
  Users,
  Star,
  MapPin,
  Laptop,
  TableProperties,
  UserCheck,
  Award,
  BookOpenCheck,
  HeartHandshake,
  Trophy,
  CalendarDays,
  Layers,
} from "lucide-react";
import {
  BRAND,
  CATEGORIES,
  USPS,
  STATS,
  UNIVERSITIES,
  WA_LINK,
} from "../lib/constants";
import { fetchPublic } from "../lib/content";
import { Button } from "../components/ui/button";
import { CourseCard } from "../components/CourseCard";
import { SectionHeader } from "../components/SectionHeader";
import { Alumni } from "../components/Alumni";
import { Testimonials } from "../components/Testimonials";
import { SuccessStories } from "../components/SuccessStories";
import { DiscountCTA, DISCOUNT_CTA_DESC, LAUNCH_CTA_DESC, useDiscountMeta } from "../components/DiscountCTA";
import { AreaLandingSection } from "../components/AreaLandingSection";

const ICONS = {
  GraduationCap, Monitor, Timer, Sparkles, Briefcase, School,
  Infinity: InfinityIcon, BriefcaseBusiness, Sparkle, MessageSquareHeart,
};

export default function Home() {
  const [COURSES, setCourses] = useState([]);
  const launchMode = useDiscountMeta().mode === "launch";
  useEffect(() => {
    fetchPublic("courses").then(setCourses).catch(() => setCourses([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-28 pb-20 md:pt-32 md:pb-24 overflow-hidden" data-testid="home-hero">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_skill-success-hub/artifacts/qaqjet42_1782511996262.png"
            alt="TASSEI professional team"
            className="w-full h-full object-cover object-top"
          />
          {/* Premium overlapping gradients */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F]/85 via-[#0A192F]/55 to-[#00B37E]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/30 to-transparent" />
          <div className="absolute inset-0 tassei-dot-bg opacity-20 mix-blend-overlay" />
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#00B37E]/30 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#00B37E]/15 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center min-h-[560px] md:min-h-[640px]">
          <div className="lg:col-span-8 text-white">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/20 bg-white/8 backdrop-blur text-[#7CF0BD] text-xs font-bold uppercase tracking-[0.22em] fade-up">
              <MapPin className="w-3.5 h-3.5" /> Best Computer Institute in Sangam Vihar
            </div>
            <h1 className="mt-5 font-heading font-extrabold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight fade-up">
              From{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#7CF0BD] via-[#00B37E] to-[#34D399] bg-clip-text text-transparent">
                  Dreams
                </span>
                <svg className="absolute -bottom-3 left-0 w-full" height="14" viewBox="0 0 200 14" fill="none">
                  <path d="M2 8C40 2 80 2 120 8C140 11 170 11 198 6" stroke="#00B37E" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>{" "}
              to <br className="hidden md:block"/>
              Real{" "}
              <span className="text-[#7CF0BD]">Success</span>.
            </h1>
            <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-2xl fade-up">
              <span className="font-semibold text-white">TASSEI</span> — {BRAND.fullForm}. A trusted <span className="font-semibold text-white">computer centre in Sangam Vihar</span> offering job-oriented computer classes, AI-powered programs, professional certifications &amp; university degrees — all under one career ecosystem.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-up">
              <a
                href={WA_LINK("Hi TASSEI, I want a free consultation")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-consultation-cta"
              >
                <Button className="bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-12 px-7 text-base shadow-lg shadow-[#00B37E]/30 hover:shadow-xl transition-all">
                  <MessageCircle className="w-5 h-5 mr-2" /> Book Free Consultation
                </Button>
              </a>
              <Link to="/courses" data-testid="hero-explore-courses">
                <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white hover:text-[#0A192F] font-semibold rounded-full h-12 px-7 text-base transition-all bg-white/5 backdrop-blur">
                  Explore Courses <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="mt-6 fade-up">
              <DiscountCTA testId="hero-discount-cta" descClass="text-white/80" />
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-2xl fade-up">
              {STATS.map((s) => (
                <div key={s.label} className="border-l-2 border-[#00B37E] pl-3" data-testid={`stat-${s.label}`}>
                  <div className="font-heading text-2xl sm:text-3xl font-extrabold text-white">{s.value}</div>
                  <div className="text-xs text-white/70 mt-0.5 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex lg:col-span-4 relative justify-end">
            <div className="space-y-4 w-full max-w-sm">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-5 py-4 shadow-xl text-white">
                <div className="w-11 h-11 rounded-full bg-[#00B37E]/25 flex items-center justify-center">
                  <Star className="w-5 h-5 text-[#7CF0BD]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">Lifetime</div>
                  <div className="text-sm font-bold">Free Practice After Course</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-5 py-4 shadow-xl text-white">
                <div className="w-11 h-11 rounded-full bg-[#00B37E]/25 flex items-center justify-center">
                  <BriefcaseBusiness className="w-5 h-5 text-[#7CF0BD]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">Until placed</div>
                  <div className="text-sm font-bold">Job Assistance Guaranteed</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-5 py-4 shadow-xl text-white">
                <div className="w-11 h-11 rounded-full bg-[#00B37E]/25 flex items-center justify-center">
                  <School className="w-5 h-5 text-[#7CF0BD]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">18+ Universities</div>
                  <div className="text-sm font-bold">Online &amp; Distance Degrees</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATE YOUR DISCOUNT BANNER */}
      <section className="relative py-16 md:py-20 overflow-hidden bg-[#0A192F]" data-testid="discount-banner-section">
        <div className="absolute inset-0 tassei-dot-bg opacity-15" />
        <div className="absolute -top-24 right-0 w-[420px] h-[420px] rounded-full bg-[#FF7A00]/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-[#00B37E]/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFD66B] text-xs font-bold uppercase tracking-[0.22em]">
              <Sparkles className="w-3.5 h-3.5" /> {launchMode ? "Website launch offer" : "Earn your own discount"}
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight" data-testid="discount-banner-heading">
              {launchMode ? (
                <>Flat <span className="bg-gradient-to-r from-[#FFB800] via-[#FF7A00] to-[#FF3D71] bg-clip-text text-transparent">50% OFF</span> for the first 100 students.</>
              ) : (
                <>Smart students <span className="bg-gradient-to-r from-[#FFB800] via-[#FF7A00] to-[#FF3D71] bg-clip-text text-transparent">pay less</span>. Take the 15-question challenge.</>
              )}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl">{launchMode ? LAUNCH_CTA_DESC : DISCOUNT_CTA_DESC}</p>
            <div className="mt-8">
              <DiscountCTA testId="banner-discount-cta" size="lg" showDesc={false} />
            </div>
          </div>
          {launchMode ? (
            <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="discount-banner-launch">
              {[["50%", "Flat discount on course fees"], ["100", "Seats only — first come, first served"], ["2 min", "Just share your details to claim"], ["0", "Questions. No assessment needed"]].map(([big, small]) => (
                <div key={small} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur px-5 py-5">
                  <div className="font-heading text-3xl font-black text-[#FFD66B]">{big}</div>
                  <div className="text-xs uppercase tracking-wider text-white/60 font-bold mt-1 leading-snug">{small}</div>
                </div>
              ))}
            </div>
          ) : (
          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="discount-banner-slabs">
            {[["13–15", "30%"], ["10–12", "25%"], ["9", "20%"], ["8", "15%"], ["7", "10%"], ["5–6", "5%"]].map(([range, pct]) => (
              <div key={range} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur px-5 py-4 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-white/60 font-bold">{range} correct</span>
                <span className="font-heading text-2xl font-black text-[#FFD66B]">{pct}</span>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* SANGAM VIHAR - LOCAL SEO / FEATURED COMPUTER PROGRAMS */}
      <section
        className="py-20 md:py-24 bg-white relative overflow-hidden"
        data-testid="sangam-vihar-section"
        aria-labelledby="sangam-vihar-heading"
      >
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#00B37E]/8 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#0A66C2]/8 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00B37E]/10 text-[#00B37E] text-xs font-bold uppercase tracking-[0.22em]">
              <MapPin className="w-3.5 h-3.5" /> Serving Sangam Vihar, South Delhi
            </div>
            <h2
              id="sangam-vihar-heading"
              className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0A192F] leading-[1.05]"
            >
              Best <span className="text-[#00B37E]">Computer Institute in Sangam Vihar</span> for job-oriented careers.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
              Looking for the right <strong>computer classes in Sangam Vihar</strong>? TASSEI.in is a
              career-focused <strong>computer centre in Sangam Vihar</strong> that trains students, homemakers and
              job seekers with <strong>job-oriented computer programs</strong> — from foundational computer
              literacy to advanced Excel, digital skills and placement assistance until you get a job.
            </p>
          </div>

          {/* 3 flagship programs */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Laptop,
                tag: "Job-Oriented Computer Program",
                title: "Diploma in Computer Application (DCA)",
                desc: "The most popular job-oriented computer course in Sangam Vihar. Master MS Office, Tally, internet, typing, presentations and computer fundamentals — everything you need for office, admin and data-entry jobs.",
                bullets: ["MS Word, Excel, PowerPoint", "Tally + Basic Accounting", "Internet, Email & Typing", "Placement Assistance"],
                cta: "Enrol in DCA — Sangam Vihar",
                wa: "Hi TASSEI, I want to join the Diploma in Computer Application (DCA) at your Sangam Vihar centre. Please share details.",
              },
              {
                icon: TableProperties,
                tag: "Career Booster Program",
                title: "Mastery in Advanced Excel",
                desc: "Career-changing advanced Excel training in Sangam Vihar. Pivot tables, dashboards, complex formulas, Power Query and VBA basics — the exact skills recruiters look for in analyst, MIS and back-office roles.",
                bullets: ["Formulas & Lookups", "Pivot Tables & Dashboards", "Power Query & VBA basics", "Real-world case studies"],
                cta: "Start Advanced Excel",
                wa: "Hi TASSEI, I want to join Mastery in Advanced Excel at your Sangam Vihar centre. Please share fees & schedule.",
              },
              {
                icon: UserCheck,
                tag: "Empowering Women",
                title: "Digital Literacy for Women",
                desc: "A dedicated digital literacy program in Sangam Vihar for women — homemakers, mothers and re-starters. Learn smartphones, internet, digital payments, online safety, WhatsApp business basics and confidence-building for career growth.",
                bullets: ["Smartphone & Internet Basics", "Digital Payments & UPI", "WhatsApp Business & Online Safety", "Women-only friendly batches"],
                cta: "Join Women's Batch",
                wa: "Hi TASSEI, I want to join the Digital Literacy for Women program in Sangam Vihar. Please share details.",
                accent: "women",
              },
            ].map((p, idx) => {
              const Icon = p.icon;
              const isWomen = p.accent === "women";
              return (
                <article
                  key={p.title}
                  data-testid={`sangam-program-${idx}`}
                  className={`group relative rounded-2xl border-2 p-6 md:p-7 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex flex-col ${
                    isWomen
                      ? "border-pink-300 bg-gradient-to-br from-pink-50 via-rose-50 to-pink-50/50 hover:border-pink-400 hover:shadow-pink-500/15"
                      : "border-slate-200 bg-white hover:border-[#00B37E]/40 hover:shadow-[#0A192F]/10"
                  }`}
                >
                  {isWomen && (
                    <span
                      aria-hidden="true"
                      className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full bg-pink-600 text-white text-[9px] font-black tracking-[0.18em] uppercase shadow-sm"
                    >
                      For Women
                    </span>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${
                        isWomen
                          ? "bg-gradient-to-br from-pink-500 to-rose-500 shadow-pink-500/30"
                          : "bg-gradient-to-br from-[#00B37E] to-[#009166] shadow-[#00B37E]/25"
                      }`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span
                      className={`text-[10px] font-black tracking-[0.16em] uppercase px-2.5 py-1 rounded-full ${
                        isWomen
                          ? "text-pink-700 bg-pink-100 ring-1 ring-pink-200"
                          : "text-[#0A66C2] bg-[#0A66C2]/10"
                      }`}
                    >
                      {p.tag}
                    </span>
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-extrabold text-[#0A192F] leading-tight">
                    {p.title}
                  </h3>
                  <p className={`mt-2.5 text-sm leading-relaxed ${isWomen ? "text-[#6B4457]" : "text-[#475569]"}`}>
                    {p.desc}
                  </p>
                  <ul className="mt-4 space-y-1.5">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-[#334155]">
                        <CheckCircle2
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isWomen ? "text-pink-600" : "text-[#00B37E]"}`}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div
                    className={`mt-6 flex flex-col sm:flex-row gap-2 pt-4 border-t ${
                      isWomen ? "border-pink-200" : "border-slate-100"
                    }`}
                  >
                    <a
                      href={WA_LINK(p.wa)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`sangam-program-cta-${idx}`}
                      className="flex-1"
                    >
                      <Button
                        className={`w-full text-white font-semibold rounded-full h-10 ${
                          isWomen
                            ? "bg-pink-600 hover:bg-pink-700"
                            : "bg-[#00B37E] hover:bg-[#009166]"
                        }`}
                      >
                        <MessageCircle className="w-4 h-4 mr-1.5" /> {p.cta}
                      </Button>
                    </a>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Why Sangam Vihar students choose TASSEI */}
          <div className="mt-14 rounded-3xl bg-gradient-to-br from-[#F8FAF9] to-white border border-slate-200 p-6 md:p-10">
            <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
              <div className="md:col-span-5">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#00B37E]">
                  Why Sangam Vihar chooses TASSEI
                </div>
                <h3 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold text-[#0A192F] leading-tight">
                  A local <span className="text-[#0A66C2]">computer centre</span> with big-city career outcomes.
                </h3>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                  Whether you are a student in Sangam Vihar hunting your first job, a working professional
                  upgrading your Excel skills, or a woman starting her digital journey — every TASSEI computer
                  program is built with one goal in mind: <strong>career growth</strong>.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/courses?cat=ai-computer" data-testid="sangam-all-programs-cta">
                    <Button className="bg-[#0A192F] hover:bg-[#0d2545] text-white rounded-full h-11 px-5 font-semibold">
                      See all computer programs <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                  <a
                    href={WA_LINK(
                      "Hi TASSEI, I'm from Sangam Vihar. I want to know about your job-oriented computer courses."
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="sangam-consult-cta"
                  >
                    <Button
                      variant="outline"
                      className="border-2 border-[#00B37E] text-[#00B37E] hover:bg-[#00B37E] hover:text-white rounded-full h-11 px-5 font-semibold"
                    >
                      <MessageCircle className="w-4 h-4 mr-1.5" /> Free WhatsApp Consultation
                    </Button>
                  </a>
                </div>
              </div>
              <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: InfinityIcon, t: "Lifetime Free Practice", d: "Keep coming back to the centre for free practice — for life, even after your course ends." },
                  { icon: Award, t: "Verified Certificate", d: "Industry-verifiable TASSEI certificate you can proudly add to your CV and LinkedIn." },
                  { icon: BookOpenCheck, t: "Practical Learning", d: "Hands-on, project-first training on real files and real workflows — no boring theory dumps." },
                  { icon: Trophy, t: "100+ Students Placed", d: "Over 100 Sangam Vihar students placed till now across offices, shops, BPOs and startups." },
                  { icon: HeartHandshake, t: "Permanent Discounted Fees", d: "Special always-on discount for Divyang (disabled) students, housewives and students from armed-forces families." },
                  { icon: CalendarDays, t: "Monthly Personality Workshops", d: "Free personality-development, spoken-English and interview workshops every month." },
                  { icon: Briefcase, t: "Placement Assistance", d: "We stay with you till you land a job — CV help, mock interviews & employer referrals." },
                  { icon: Timer, t: "Flexible Batches", d: "Morning, evening & weekend batches — built for students, working folks & homemakers." },
                  { icon: Layers, t: "One-Stop Education Hub", d: "Free higher-education counselling, Open Board 10 & 12 admissions and subjective coaching — all under one roof." },
                ].map((f) => {
                  const FIcon = f.icon;
                  return (
                    <div
                      key={f.t}
                      className="rounded-xl bg-white border border-slate-200 p-4 hover:border-[#00B37E]/50 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-[#00B37E]/10 flex items-center justify-center flex-shrink-0">
                          <FIcon className="w-4 h-4 text-[#00B37E]" />
                        </div>
                        <div className="font-bold text-sm text-[#0A192F] leading-tight">{f.t}</div>
                      </div>
                      <p className="mt-2 text-xs text-[#64748B] leading-relaxed">{f.d}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UNIVERSITIES MARQUEE */}
      {/* AREAS WE SERVE - LOCAL SEO */}
      <AreaLandingSection />

      <section className="border-y border-slate-200 bg-[#F8FAF9] py-8" data-testid="universities-marquee">
        <div className="max-w-7xl mx-auto px-5 md:px-10 mb-4 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#00B37E]">
            18+ University Partners
          </div>
          <div className="text-xs text-[#64748B] hidden sm:block">Distance &amp; Online Higher Education</div>
        </div>
        <div className="overflow-hidden">
          <div className="marquee-track flex gap-4 w-max">
            {[...UNIVERSITIES, ...UNIVERSITIES].map((u, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-6 py-3 bg-white border border-slate-200 rounded-full whitespace-nowrap"
              >
                <School className="w-4 h-4 text-[#00B37E]" />
                <span className="text-sm font-semibold text-[#0A192F]">{u}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 md:py-28" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <SectionHeader
            eyebrow="Our Education Categories"
            title="One ecosystem. Every career path."
            desc="From foundational schooling to AI-powered upskilling and university degrees — TASSEI covers every stage of your learning journey."
          />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((cat) => {
              const Icon = ICONS[cat.icon] || GraduationCap;
              return (
                <Link
                  key={cat.id}
                  to={`/courses?cat=${cat.id}`}
                  data-testid={`category-${cat.id}`}
                  className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#0A192F]/10 transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 via-[#0A192F]/30 to-transparent" />
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-[#00B37E] text-white text-xs font-bold">
                      {cat.stat}
                    </div>
                    <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#00B37E]" />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl font-bold text-[#0A192F]">{cat.name}</h3>
                    <p className="text-sm text-[#475569] mt-2 leading-relaxed">{cat.desc}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#00B37E] group-hover:gap-2.5 transition-all">
                      View courses <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE TASSEI - BENTO */}
      <section className="py-20 md:py-28 bg-[#0A192F] relative overflow-hidden" data-testid="why-choose-section">
        <div className="absolute inset-0 tassei-dot-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#7CF0BD]">Why Choose TASSEI</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-white leading-[1.05]">
              We don&apos;t just teach. <br/>We{" "}
              <span className="text-[#00B37E]">walk with you</span> till you succeed.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {USPS.map((u, idx) => {
              const Icon = ICONS[u.icon] || Sparkle;
              return (
                <div
                  key={u.title}
                  data-testid={`usp-${idx}`}
                  className="relative bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur rounded-2xl border border-white/10 p-6 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#00B37E] flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold text-white">{u.title}</h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">{u.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ALL COURSES BY CATEGORY - IN SEQUENCE */}
      <section className="py-20 md:py-28" data-testid="all-courses-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <SectionHeader
              eyebrow="Our Course Catalogue"
              title="All programs in one place."
              desc="Browse every course we offer — organized category by category. Tap any card to enquire on WhatsApp."
            />
            <Link to="/courses" data-testid="see-all-courses">
              <Button variant="outline" className="border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-semibold rounded-full h-11 px-6">
                Filter &amp; search <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="mt-12 space-y-16">
            {CATEGORIES.map((cat, ci) => {
              const Icon = ICONS[cat.icon] || GraduationCap;
              const list = COURSES.filter((c) => c.category === cat.id);
              if (list.length === 0) return null;
              return (
                <div key={cat.id} data-testid={`category-block-${cat.id}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#64748B]">
                      0{ci + 1}
                    </div>
                    <div className="h-px flex-1 bg-slate-200" />
                    <div className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-[#00B37E]/10 text-[#00B37E]">
                      <Icon className="w-4 h-4" />
                      <span className="text-xs font-bold uppercase tracking-[0.18em]">{cat.stat}</span>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6">
                    <div>
                      <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-[#64748B] mt-1.5 max-w-2xl">{cat.desc}</p>
                    </div>
                    <Link
                      to={`/courses?cat=${cat.id}`}
                      data-testid={`view-category-${cat.id}`}
                      className="text-sm font-semibold text-[#00B37E] hover:gap-2 inline-flex items-center gap-1.5 transition-all"
                    >
                      View all in {cat.name} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map((c) => (
                      <CourseCard key={c.id} course={c} categoryName={cat.name} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ALUMNI */}
      <Alumni />

      {/* SUCCESS STORIES */}
      <SuccessStories />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* MISSION */}
      <section className="py-20 md:py-28 bg-[#F8FAF9]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 relative">
              <img
                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=900&q=80"
                alt="Job placement handshake — interview success"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7CF0BD]">Real outcomes</div>
                <p className="mt-1 font-heading text-base font-bold leading-snug">
                  Sapno se safalta tak — student to placed professional.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="Our Mission" title="Building India's No. 1 Career Success Ecosystem." />
            <p className="mt-5 text-lg text-[#475569] leading-relaxed">
              We exist to make career success accessible to every Indian — especially the rural youth. Through
              awareness seminars in schools &amp; colleges, hands-on training, mentorship and university partnerships,
              TASSEI is laying down the railway tracks that take dreams to destinations.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Target, t: "Rural Outreach", d: "Awareness seminars across schools & colleges" },
                { icon: Users, t: "Industry Experts", d: "Workshops led by real-world mentors" },
                { icon: GraduationCap, t: "Lifelong Learning", d: "Practice & upskill — forever, free" },
                { icon: CheckCircle2, t: "Real Outcomes", d: "Placement support until you get a job" },
              ].map((i, idx) => {
                const Icon = i.icon;
                return (
                  <div key={idx} className="flex gap-3 items-start">
                    <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#00B37E]" />
                    </div>
                    <div>
                      <div className="font-bold text-[#0A192F] text-sm">{i.t}</div>
                      <div className="text-sm text-[#64748B] mt-0.5">{i.d}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#00B37E] to-[#0A192F] p-10 md:p-16 text-white">
            <div className="absolute inset-0 opacity-20 tassei-dot-bg" />
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#7CF0BD]">Start Today</div>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
                  Apne sapno ko safalta tak le jaane ka <br className="hidden md:block"/>pehla step abhi lijiye.
                </h2>
                <p className="mt-4 text-white/85 max-w-2xl text-lg">
                  Book a free 15-minute consultation on WhatsApp. We&apos;ll guide you to the right course, university or career path.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <a
                  href={WA_LINK("Hi TASSEI, I want a free consultation")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cta-section-whatsapp"
                >
                  <Button className="bg-white hover:bg-slate-100 text-[#0A192F] font-bold rounded-full h-14 px-8 text-base shadow-2xl">
                    <MessageCircle className="w-5 h-5 mr-2" /> Book Free Consultation
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
